'use client';

import React, { useState } from "react";
import { api, errText } from "./api";
import { AField, AInput, AError, ABtn, ALink, DevCodeHint, StepTag } from "./authUi";

// Team-invite acceptance. A person who was invited enters their
// number, gets a one-time code, and sets a PIN — then they're in.
export const JoinFlow = ({ onAuthed, onBack }) => {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async (e) => {
    e?.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/invite/start", { phone });
      setName(data.name || "");
      setOrgName(data.org_name || "");
      setDevCode(data.dev_code || null);
      setStep(2);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/invite/verify", { phone, code });
      setStep(3);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const complete = async (e) => {
    e.preventDefault();
    if (pin !== pin2) return setError("The two PINs don't match.");
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/invite/complete", { phone, pin });
      onAuthed(data);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  if (step === 1) {
    return (
      <form onSubmit={start} className="space-y-4">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Join your team</h2>
          <StepTag step={1} total={3} />
        </div>
        <p className="text-sm text-zinc-500">
          Enter the mobile number you were invited with. We'll send a one-time code to verify it.
        </p>
        <AError>{error}</AError>
        <AField label="Mobile number">
          <AInput
            data-testid="join-phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="98xxxxxxxx"
            inputMode="numeric"
            autoComplete="tel"
          />
        </AField>
        <ABtn data-testid="join-start-button" type="submit" busy={busy}>Send me the code</ABtn>
        <div className="pt-1 text-center">
          <ALink data-testid="join-back-link" onClick={onBack}>Back to login</ALink>
        </div>
      </form>
    );
  }

  if (step === 2) {
    return (
      <form onSubmit={verify} className="space-y-4">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Check your SMS</h2>
          <StepTag step={2} total={3} />
        </div>
        <p className="text-sm text-zinc-500">
          {name ? <>Welcome, <strong className="text-zinc-800">{name}</strong>. </> : null}
          {orgName ? <>You're joining <strong className="text-zinc-800">{orgName}</strong>. </> : null}
          We sent a 6-digit code to <strong className="text-zinc-800">{phone}</strong>.
        </p>
        <DevCodeHint code={devCode} />
        <AError>{error}</AError>
        <AField label="6-digit code">
          <AInput
            data-testid="join-otp-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            inputMode="numeric"
            className="text-center text-lg tracking-[0.5em]"
          />
        </AField>
        <ABtn data-testid="join-verify-button" type="submit" busy={busy} disabled={code.length !== 6}>Verify</ABtn>
        <div className="flex items-center justify-between pt-1">
          <ALink data-testid="join-otp-back-link" onClick={() => { setError(""); setStep(1); }}>Back</ALink>
          <ALink data-testid="join-resend-button" onClick={() => start()}>Resend code</ALink>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={complete} className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Set your PIN</h2>
        <StepTag step={3} total={3} />
      </div>
      <p className="text-sm text-zinc-500">You'll log in with your mobile number and this PIN from now on.</p>
      <AError>{error}</AError>
      <AField label="Choose a PIN (4–6 digits)">
        <AInput data-testid="join-pin-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} type="password" inputMode="numeric" placeholder="••••" />
      </AField>
      <AField label="Type it again">
        <AInput data-testid="join-pin-confirm-input" value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 6))} type="password" inputMode="numeric" placeholder="••••" />
      </AField>
      <ABtn data-testid="join-complete-button" type="submit" busy={busy} disabled={pin.length < 4}>Join the team</ABtn>
    </form>
  );
};
